/**
 * RIJ promo widget — site-wide promo bubble (custom-promo-widget.js).
 * Content Cards (promo/announcement/story) drive the pop → roll/expand →
 * collapse animation; template gating comes from payload.active (Liquid).
 * All copy/timing configurable from section settings.
 */
(function () {
  const NS = window.RIJ = window.RIJ || {};

  const SCROLL_TRIGGER_PX = 400; // first scroll that counts as "browsing"

  // "YYYY-MM-DD" parses as UTC midnight in JS — rebuild as LOCAL midnight so
  // date windows match the shopper's own timezone, not London's.
  function parseDate(value) {
    const s = String(value || '').trim();
    if (!s) return null;
    const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    let d;
    if (dateOnly) d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    else d = new Date(s.replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d.getTime();
  }

  function campaignActive(c) {
    const now = Date.now();
    const start = parseDate(c.start_at);
    const end = parseDate(c.end_at);
    if (start !== null && now < start) return false;
    if (end !== null && now > end) return false;
    return true;
  }

  function readJSON(el) {
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  // Identity of a card for shown/clicked memory — code beats url beats headline.
  function cardKey(c) {
    return c.code || c.url || c.headline || 'card';
  }

  NS.initPromoWidget = function () {
    const root = document.querySelector('.rij-sticky-gift');
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const dataEl = root.querySelector('script[data-rij-promo-widget]');
    if (!dataEl) return;
    const payload = readJSON(dataEl);

    // Template not enabled (section settings) — stay fully hidden.
    if (!payload || payload.active === false) return;

    const cards = (payload.cards || []).filter(campaignActive);
    if (!cards.length) return;

    // Card-flip UI lives in the small window (popover): when more than one
    // card is active, the popover shows a stack hint + "n/N" counter, and
    // clicking/swiping the card flips to the next one. The big message bar
    // always opens the detail window (single or multi card).
    const multiCard = cards.length > 1;
    const popCounter = root.querySelector('.rij-sticky-gift__pop-counter');
    let cardIndex = -1;

    function renderCard(c) {
      cardIndex = cards.indexOf(c);
      fillCard(c);
    }
    function flipCard(dir) {
      if (!multiCard) return;
      const w = cardStep() || 264;
      // Default behavior: instant + scroll-snap aligns the next card.
      cardsEl.scrollBy({ left: dir * w });
      updateCounter(); // arrows update the counter directly (scroll events not
      // reliably fired by programmatic scrolls in every environment)
    }

    const labels = payload.labels || { hello: 'Hello!', bye: 'Bye!' };

    // Pick a random line from a multi-line label setting (one per line).
    function pickLabel(text) {
      const lines = String(text || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!lines.length) return '';
      return lines[Math.floor(Math.random() * lines.length)];
    }

    // Guest time-slot (visitor's local hour).
    function timeSlot() {
      const h = new Date().getHours();
      if (h >= 5 && h < 11) return 'morning';
      if (h >= 11 && h < 17) return 'afternoon';
      if (h >= 17 && h < 23) return 'evening';
      return 'night';
    }

    // Greeting with personality:
    //  - logged-in members  -> "Hi, {firstName}!" (personal, first-party)
    //  - guests             -> time-slot greeting from section settings
    //  - bye/peek           -> plain random labels as configured
    function greetingFor(kind) {
      if (kind === 'hello') {
        const cs = NS.customerState;
        if (cs && cs.isLoggedIn()) {
          const name = cs.firstName();
          if (name) return 'Hi, ' + name + '!';
        }
        const tg = payload.timeGreetings || {};
        const slot = timeSlot();
        if (tg[slot]) return pickLabel(tg[slot]);
      }
      return pickLabel(labels[kind]);
    }

    // While resting (peek), the emoji periodically pops a small label.
    let peekLabelTimer = null;
    function startPeekLabels() {
      stopPeekLabels();
      if (!labels.peek) return;
      peekLabelTimer = setInterval(() => {
        if (!root.classList.contains('is-peek')) return;
        showSpeech(pickLabel(labels.peek));
        setTimeout(hideSpeech, 1500);
      }, 4500);
    }
    function stopPeekLabels() {
      if (peekLabelTimer) {
        clearInterval(peekLabelTimer);
        peekLabelTimer = null;
      }
    }

    const b = payload.behaviors || {};
    const FIRST_DELAY_MS = b.firstDelayMs || 4000;
    const HOLD_MS = b.holdMs || 9000;
    const COOLDOWN_MS = b.cooldownMs || 45000;
    const MAX_SHOWS = b.maxShows || 3;

    // Pages without a sticky bar (non-product) float the bubble at the screen
    // corner; product pages ride the bar's top edge. Layout only, no behavior
    // difference between viewports.
    const hasStickyBar = !!document.querySelector('sticky-add-to-cart');
    root.classList.toggle('no-bar', !hasStickyBar);

    // Wheel-physics spin: as the bubble rolls, the emoji inside rotates in sync
    // with the distance travelled — angle = roll distance / radius (22px) in
    // radians, converted to degrees. Direction from the section position.
    // Restored from the original mobile "rolling wheel" behavior.
    {
      const cs = getComputedStyle(root);
      const ratio = parseFloat(cs.getPropertyValue('--rij-gift-roll-ratio')) || 0.33;
      const dir = parseFloat(cs.getPropertyValue('--rij-gift-roll-dir')) || 1;
      // On small screens the configured 33% roll is shorter than the message
      // bar it stretches into — the roll would read as "pop open". Push the
      // mobile roll to 50% so the rolling dominates the expanding.
      const effRatio = window.innerWidth <= 749 ? 0.5 : ratio;
      const roll = (window.innerWidth - 24) * effRatio;
      // Round the spin to whole turns so the emoji ends upright again.
      const turns = Math.round((roll / 22) / (2 * Math.PI));
      const spinDeg = turns * 360;
      root.style.setProperty('--rij-gift-spin', (dir * spinDeg).toFixed(1) + 'deg');
      if (window.innerWidth <= 749) {
        root.style.setProperty('--rij-gift-roll', roll.toFixed(0) + 'px');
        root.style.setProperty('--rij-gift-roll-x', (dir * roll).toFixed(0) + 'px');
      }
    }

    // Horizontal alignment: the bubble's left/center/right is relative to the
    // STICKY BAR — a centered ~600px bar on DESKTOP product pages. On mobile
    // the bar is full-width, so viewport positioning is already correct; and
    // the bar's in-flow rect there is unreliable, which used to shove the
    // widget off-screen. Only align on desktop.
    function alignToBar() {
      if (window.innerWidth <= 749) return; // mobile: full-width bar, no align
      const bar = document.querySelector(
        'sticky-add-to-cart .sticky-add-to-cart__bar'
      );
      if (!bar) return; // no bar — section CSS handles it
      const br = bar.getBoundingClientRect();
      if (br.width < 10) return; // bar not laid out yet
      const pos = getComputedStyle(root).getPropertyValue('--rij-gift-pos').trim() || 'left';
      const offsetX = parseInt(
        getComputedStyle(root).getPropertyValue('--rij-gift-offset-x') || '0',
        10
      ) || 0;
      if (pos === 'right') {
        root.style.left = br.right - 12 - 52 + offsetX + 'px';
        root.style.right = 'auto';
      } else if (pos === 'center') {
        root.style.left = br.left + br.width / 2 - 26 + offsetX + 'px';
        root.style.right = 'auto';
      } else {
        root.style.left = br.left + 12 + offsetX + 'px';
        root.style.right = 'auto';
      }
    }
    // Bar is position:fixed — its rect only changes on resize, not scroll.
    // (Calling alignToBar on scroll would run getBoundingClientRect on every
    // scroll frame for no change.)
    alignToBar();
    window.addEventListener('resize', alignToBar);

    const unit = root.querySelector('.rij-sticky-gift__unit');
    const giftEl = root.querySelector('.rij-sticky-gift__gift');
    const titleEl = root.querySelector('.rij-sticky-gift__title');
    const speechEl = root.querySelector('.rij-sticky-gift__speech');

    // Speech bubbles ("Hello!" on pop-out, "Bye!" before shrink-back).
    function showSpeech(text) {
      if (!speechEl) return;
      speechEl.textContent = text;
      speechEl.classList.add('is-show');
    }
    function hideSpeech() {
      if (!speechEl) return;
      speechEl.classList.remove('is-show');
    }

    // Small detail window (popover) — one full card per slot, in a
    // horizontally scrollable row (built by JS from the payload).
    const popover = root.querySelector('.rij-sticky-gift__popover');
    const closeBtn = popover.querySelector('.rij-sticky-gift__close');
    const cardsEl = popover.querySelector('.rij-sticky-gift__cards');

    // Large detail window (modal)
    const modal = root.querySelector('.rij-sticky-gift__modal');
    const modalClose = modal.querySelector('.rij-sticky-gift__modal-close');
    const modalEmoji = modal.querySelector('.rij-sticky-gift__modal-emoji');
    const modalHeadline = modal.querySelector('.rij-sticky-gift__modal-headline');
    const modalMessage = modal.querySelector('.rij-sticky-gift__modal-message');
    const modalCodeRow = modal.querySelector('.rij-sticky-gift__modal-code');
    const modalCodeEl = modal.querySelector('.rij-sticky-gift__modal-code-value');
    const modalCopy = modal.querySelector('.rij-sticky-gift__modal-copy');
    const modalApply = modal.querySelector('.rij-sticky-gift__modal-apply');

    // Per-visit memory only (in-memory): every page load starts fresh, so the
    // widget always gets its chance to show (up to MAX_SHOWS times this visit).
    // Engaged cards are skipped within this visit; a reload resets everything.
    // No sessionStorage — a previously clicked card must never silence the
    // widget forever.
    const shown = [];
    const clicked = new Set();

    let queue = 0; // rotation cursor
    let current = null; // card behind the gift right now
    let firstTimer = null;
    let holdTimer = null;
    let cooldownTimer = null;
    let speechTimer = null;
    let open = false;

    function setState(state) {
      root.classList.remove('is-peek', 'is-shown');
      if (state) root.classList.add('is-' + state);
    }

    // Resting state: PEEK — the round bubble sits tucked behind the bar with
    // just its top arc showing (playful hint, never parked in full view).
    // Engaged cards are skipped in future rotations, and once every card was
    // engaged the widget stays peeking (nothing to promote anymore).
    function restingState() {
      return 'peek';
    }

    // Build one complete card (emoji, headline, message, code+copy, action).
    function buildCard(c) {
      const card = document.createElement('div');
      card.className = 'rij-sticky-gift__card';
      card.setAttribute('role', 'listitem');

      const isPromo = c.type === 'promo';
      const href = isPromo
        ? '/discount/' + encodeURIComponent(c.code || '')
        : c.url || '';

      if (c.type === 'story') {
        const emoji = document.createElement('p');
        emoji.className = 'rij-sticky-gift__card-emoji';
        emoji.textContent = c.emoji || '💛';
        card.appendChild(emoji);
      }
      if (c.headline) {
        const hl = document.createElement('p');
        hl.className = 'rij-sticky-gift__card-headline';
        hl.textContent = c.headline;
        card.appendChild(hl);
      }
      if (c.message) {
        const msg = document.createElement('p');
        msg.className = 'rij-sticky-gift__card-message';
        msg.textContent = c.message;
        card.appendChild(msg);
      }
      if (isPromo && c.code) {
        const code = document.createElement('div');
        code.className = 'rij-sticky-gift__card-code';
        const val = document.createElement('span');
        val.className = 'rij-sticky-gift__card-code-value';
        val.textContent = c.code;
        const copy = document.createElement('button');
        copy.type = 'button';
        copy.className = 'rij-sticky-gift__copy';
        copy.textContent = 'Copy';
        code.append(val, copy);
        card.appendChild(code);
      }
      if (href) {
        const a = document.createElement('a');
        a.className = 'rij-sticky-gift__card-apply';
        a.href = href;
        a.textContent = c.button_text || (isPromo ? 'Use it now →' : 'Learn more →');
        card.appendChild(a);
      }
      return card;
    }

    // Render all cards into the popover row.
    function renderCards() {
      cardsEl.innerHTML = '';
      cards.forEach((c) => cardsEl.appendChild(buildCard(c)));
    }

    function cardStep() {
      const first = cardsEl.firstElementChild;
      return first ? first.getBoundingClientRect().width : 264;
    }

    function currentIndex() {
      const w = cardStep() || 264;
      const i = Math.round(cardsEl.scrollLeft / w);
      return Math.max(0, Math.min(cards.length - 1, i));
    }

    function updateCounter() {
      if (!popCounter) return;
      popCounter.textContent = multiCard
        ? currentIndex() + 1 + '/' + cards.length
        : '';
    }

    // Roll-out bar (emoji + title) and the large modal for the current card.
    function fillCard(c) {
      giftEl.textContent = c.emoji || '🎁';
      titleEl.textContent = c.bubble_text || c.headline || '';

      const isPromo = c.type === 'promo';
      const href = isPromo
        ? '/discount/' + encodeURIComponent(c.code || '')
        : c.url || '';

      modalEmoji.textContent = c.emoji || '🎁';
      modalHeadline.textContent = c.headline || '';
      modalMessage.textContent = c.message || '';
      modalCodeRow.hidden = !isPromo;
      if (isPromo) modalCodeEl.textContent = c.code || '';
      modalApply.hidden = !href;
      if (href) modalApply.href = href;
      modalApply.textContent =
        c.button_text || (isPromo ? 'Use it now →' : 'Learn more →');
    }

    // Rotate past cards the shopper already engaged with.
    function pickNextCard() {
      for (let i = 0; i < cards.length; i++) {
        const c = cards[queue % cards.length];
        queue++;
        if (!clicked.has(cardKey(c))) return c;
      }
      return null;
    }

    function startBubble() {
      if (open) return;
      const c = pickNextCard();
      if (!c) {
        // Every card was engaged — stay at rest, stop bubbling.
        setState(restingState());
        return;
      }
      current = c;
      renderCard(c);
      shown.push(cardKey(c));
      setState('shown');
      stopPeekLabels();
      // Emoji says a random hello while popping out; keep the bubble up until
      // the message bar has fully expanded (1.15s animation), then fade.
      showSpeech(greetingFor('hello'));
      clearTimeout(speechTimer);
      speechTimer = setTimeout(hideSpeech, 1250);
      if (holdTimer) clearTimeout(holdTimer);
      holdTimer = setTimeout(() => {
        if (!open) endBubble();
      }, HOLD_MS);
    }

    function endBubble() {
      // Reverse of the entrance, step for step:
      // 1) emoji waves goodbye (0.45s),
      // 2) bar shrinks back + emoji rolls back to the start edge (0.75s),
      // 3) bubble tucks back behind the bar.
      if (holdTimer) clearTimeout(holdTimer);
      giftEl.classList.add('is-waving-bye');
      showSpeech(pickLabel(labels.bye)); // random bye before shrinking back
      setTimeout(() => {
        giftEl.classList.remove('is-waving-bye');
        unit.classList.add('is-collapsing');
        giftEl.classList.add('is-rolling-back');
        setTimeout(() => {
          unit.classList.remove('is-collapsing');
          giftEl.classList.remove('is-rolling-back');
          hideSpeech();
          setState(restingState());
          startPeekLabels();
          if (shown.length >= MAX_SHOWS) return; // session cap reached — done
          if (cooldownTimer) clearTimeout(cooldownTimer);
          cooldownTimer = setTimeout(startBubble, COOLDOWN_MS);
        }, 800);
      }, 450);
    }

    // execCommand fallback: the LAN dev proxy is plain http, where the async
    // Clipboard API is unavailable (insecure context).
    function fallbackCopy(code, done) {
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.style.cssText = 'position:fixed;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        done();
      } catch (e) {
        /* ignore */
      }
      ta.remove();
    }

    function copyCode(code, btn) {
      const done = () => {
        const original = btn.textContent;
        btn.textContent = 'Copied ✓';
        setTimeout(() => {
          btn.textContent = original;
        }, 1500);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done, () => fallbackCopy(code, done));
      } else {
        fallbackCopy(code, done);
      }
    }

    // Open the popover: the horizontal row of complete cards.
    function openPopover() {
      open = true;
      if (holdTimer) clearTimeout(holdTimer);
      if (current) clicked.add(cardKey(current));
      setState(restingState()); // rolls back under the popover
      root.classList.add('is-popover-open'); // lift above the bar
      popover.hidden = false;
      popover.focus();
      updateCounter();
    }

    // Large-window detail for the card currently in view.
    function openModalForCurrent() {
      const c = cards[currentIndex()] || current;
      if (!c) return;
      current = c;
      fillCard(c);
      if (c.detail_window !== 'large') return; // small cards are fully shown in the popover
      clicked.add(cardKey(c));
      setState(restingState());
      root.classList.add('is-popover-open');
      modal.hidden = false;
      modal.focus();
    }

    function closePopover() {
      if (!open) return;
      open = false;
      popover.hidden = true;
      root.classList.remove('is-popover-open');
      endBubble();
    }

    function closeModal() {
      if (modal.hidden) return;
      modal.hidden = true;
      root.classList.remove('is-popover-open');
      endBubble();
    }

    // The whole unit (emoji + title bar) opens the popover card row.
    // stopPropagation keeps the click from bubbling to the document handler,
    // which would read it as a modal-backdrop click and instantly close it.
    unit.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!modal.hidden) return;
      if (open) closePopover();
      else openPopover();
    });

    // Card row: copy buttons copy their own code; clicking a card body opens
    // its large detail window (small cards are fully visible already).
    cardsEl.addEventListener('click', (e) => {
      const copyBtn = e.target.closest('.rij-sticky-gift__copy');
      if (copyBtn) {
        const codeRow = copyBtn.closest('.rij-sticky-gift__card-code');
        const code = codeRow
          ? codeRow.querySelector('.rij-sticky-gift__card-code-value').textContent
          : '';
        copyCode(code, copyBtn);
        return;
      }
      if (e.target.closest('.rij-sticky-gift__card-apply')) return; // link acts
      openModalForCurrent();
    });
    const flipPrev = root.querySelector('.rij-sticky-gift__flip-prev');
    const flipNext = root.querySelector('.rij-sticky-gift__flip-next');
    if (flipPrev) { flipPrev.hidden = !multiCard; flipPrev.addEventListener('click', (e) => { e.stopPropagation(); flipCard(-1); }); }
    if (flipNext) { flipNext.hidden = !multiCard; flipNext.addEventListener('click', (e) => { e.stopPropagation(); flipCard(1); }); }
    closeBtn.addEventListener('click', closePopover);
    // Counter follows the scroll position.
    cardsEl.addEventListener('scroll', () => {
      if (open) updateCounter();
    }, { passive: true });
    modalClose.addEventListener('click', closeModal);
    modalCopy.addEventListener('click', () => {
      const code = modalCodeEl.textContent;
      const done = () => {
        const original = modalCopy.textContent;
        modalCopy.textContent = 'Copied ✓';
        setTimeout(() => {
          modalCopy.textContent = original;
        }, 1500);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done, () => fallbackCopy(code, done));
      } else {
        fallbackCopy(code, done);
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeModal();
        closePopover();
      }
    });
    document.addEventListener('click', (e) => {
      if (open && !popover.contains(e.target) && !unit.contains(e.target)) closePopover();
      // Click on the modal backdrop (outside the box) closes it
      if (!modal.hidden && !e.target.closest('.rij-sticky-gift__modal-box')) closeModal();
    });

    // First appearance: delay OR first real scroll — whichever comes first.
    const firstShow = () => {
      if (firstTimer) {
        clearTimeout(firstTimer);
        firstTimer = null;
      }
      window.removeEventListener('scroll', onScroll);
      if (shown.length < MAX_SHOWS) startBubble();
    };
    const onScroll = () => {
      if (window.scrollY > SCROLL_TRIGGER_PX) firstShow();
    };

    root.hidden = false;
    renderCards(); // build the popover card row once
    // Peek at rest — the bubble shows only its top arc until the trigger
    // rolls it out into the message bar.
    setState('peek');
    startPeekLabels();

    // Optional scroll progress (section setting). The line lives on the
    // sticky add-to-cart bar when one exists (product pages) — it stays put
    // instead of riding the widget's pop/roll animation; elsewhere it falls
    // back to the widget unit. Reaching the bottom celebrates once.
    let progressCelebrated = false;
    const progressEl = root.querySelector('.rij-sticky-gift__progress');
    if (payload.scrollProgress && progressEl) {
      // Attach to the bar body itself (floats above the viewport bottom), so
      // the line hugs the bar's bottom edge and rides its slide animation.
      const stickyBar =
        document.querySelector('sticky-add-to-cart .sticky-add-to-cart__bar') ||
        document.querySelector('sticky-add-to-cart');
      if (stickyBar) {
        progressEl.classList.add('is-on-bar');
        stickyBar.appendChild(progressEl);
      } else {
        progressEl.classList.add('is-active');
      }
      const updateProgress = () => {
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        progressEl.style.width = (p * 100).toFixed(1) + '%';
        if (p >= 0.99 && !progressCelebrated) {
          progressCelebrated = true;
          showSpeech('You made it! 🎉');
          setTimeout(hideSpeech, 2200);
        }
      };
      window.addEventListener('scroll', updateProgress, { passive: true });
      window.addEventListener('resize', updateProgress);
      updateProgress();
    }

    firstTimer = setTimeout(firstShow, FIRST_DELAY_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  NS.initPromoWidget();
})();
